import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, Camera, Timer, RefreshCw, Check, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import {
  getPoseLandmarker,
  detectPose,
  detectPoseInImage,
  poseSimilarity,
  analyzePosture,
} from '../utils/poseAlignment';
import ErrorLogger from '../utils/errorLogger';
import useCameraViewport from '../utils/useCameraViewport';

/**
 * SelfTimerCamera — full-screen camera modal with self-timer support.
 *
 *  Modes : off | 3s | 5s | 10s
 *  Flow  : open modal → choose timer → tap shutter → giant countdown overlay
 *          → canvas snapshot → onCapture(File)
 *
 *  Falls back gracefully if getUserMedia is denied / unavailable:
 *  surfaces the native <input capture> instead.
 *
 *  Props:
 *    open          boolean
 *    onClose       () => void
 *    onCapture     (file: File) => void
 *    language      'fr' | 'en'
 *    facingMode    'user' | 'environment'  (default 'environment' = rear cam)
 *    ghostImageUrl string|null — URL of previous photo to overlay at low opacity
 *                                for pose alignment. If null, ghost UI is hidden.
 */
const TIMER_OPTIONS = [0, 3, 5, 10];
const GHOST_FIXED_OPACITY = 0.35;       // user-requested: no manual slider
const ONBOARDING_STORAGE_KEY = 'yumix_camera_onboarding_v2';

export default function SelfTimerCamera({
  open,
  onClose,
  onCapture,
  language = 'fr',
  facingMode: initialFacing = 'environment',
  ghostImageUrl = null,
}) {
  const fr = language === 'fr';
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const countdownRef = useRef(null);

  const [timerSec, setTimerSec] = useState(0);
  const [countdown, setCountdown] = useState(null); // null | 3,2,1
  const [facing, setFacing] = useState(initialFacing);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [capturing, setCapturing] = useState(false);

  // Iter. 406.d — iOS-safe full-viewport shell (dvh + safe-area + scroll lock)
  const { containerStyle } = useCameraViewport({ open });

  // Onboarding first-run overlay (3-step explainer). Persisted via localStorage
  // so it only appears on the very first camera open.
  const [showOnboarding, setShowOnboarding] = useState(false);

  // ─── AI Photographer (MediaPipe BlazePose) ───────────────────────────────
  const [aiAssistEnabled, setAiAssistEnabled] = useState(true); // default ON per spec
  const [aiReady, setAiReady] = useState(false);
  const [alignmentScore, setAlignmentScore] = useState(0);   // 0..1
  const [autoTriggered, setAutoTriggered] = useState(false);
  const rafRef = useRef(null);
  const referencePoseRef = useRef(null);   // landmarks of the ghost image
  const lockInTimerRef = useRef(null);     // ID of the 1.5s "hold" timer
  const lockedInRef = useRef(false);       // latch: once auto-triggered, stop loop
  const lastPoseRef = useRef(null);        // most recent live pose snapshot
  const AUTO_THRESHOLD = 0.95;
  const AUTO_HOLD_MS = 1500;

  // Request camera stream
  const startCamera = useCallback(async (requestedFacing) => {
    setError(null);
    setReady(false);
    // Stop any prior stream first
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    try {
      const constraints = {
        audio: false,
        video: {
          facingMode: { ideal: requestedFacing },
          width:  { ideal: 1920 },
          height: { ideal: 1080 },
        },
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setReady(true);
    } catch (err) {
      console.warn('[SelfTimerCamera] getUserMedia failed:', err);
      setError(err?.name || 'CameraError');
      // Iter. 404.e — Richer breadcrumb so we can distinguish iOS Safari
      // gesture violations ("NotAllowedError" with no prior prompt) from
      // genuine user denials.
      ErrorLogger.log(err, 'Scan_Postural_Camera_Init', {
        facing: requestedFacing,
        constraint: 'video@1920x1080',
        error_name: err?.name || 'unknown',
        error_message: (err?.message || '').slice(0, 200),
        is_ios_safari: isIosSafari,
        user_gesture_required: err?.name === 'NotAllowedError' && isIosSafari,
      });
    }
  }, []);

  // Iter. 404.e — iOS Safari requires `getUserMedia()` to be called inside
  // a direct user gesture (button click/touch). Calling it from a useEffect
  // when the modal opens triggers "The request is not allowed" on iOS
  // Safari (observed in Scan Radar for users on iOS 18.6.2 Safari Mobile).
  // Detect iOS Safari and gate the camera start behind an explicit
  // "Activer la caméra" button on that platform. Chrome / Android keep the
  // auto-start UX.
  const isIosSafari = (() => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    const isIos = /iPhone|iPad|iPod/.test(ua);
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    return isIos && isSafari;
  })();
  const [awaitingUserGesture, setAwaitingUserGesture] = useState(false);

  // Lifecycle : open / close
  useEffect(() => {
    if (open) {
      // iOS Safari : defer camera init until the user taps "Activer la caméra".
      // This is the documented workaround for iOS 11+ getUserMedia gesture
      // restrictions. On Chrome / Android / desktop Safari, keep auto-start.
      if (isIosSafari) {
        setAwaitingUserGesture(true);
      } else {
        startCamera(facing);
      }
      // Show first-run onboarding overlay once per device
      try {
        const seen = typeof window !== 'undefined' && window.localStorage?.getItem(ONBOARDING_STORAGE_KEY);
        if (!seen) setShowOnboarding(true);
      } catch (_) { /* localStorage unavailable — skip */ }
    } else {
      // Cleanup on close
      if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
      setCountdown(null);
      setCapturing(false);
      setAwaitingUserGesture(false);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ─── AI POSE ALIGNMENT LOOP ────────────────────────────────────────────
  // 1) Pre-compute the reference pose from the ghost image (one-shot on mount).
  // 2) Run detectPose() on each requestAnimationFrame while the modal is open.
  // 3) Maintain a "hold timer" that fires the shutter when score stays ≥ 0.95
  //    for AUTO_HOLD_MS milliseconds.
  useEffect(() => {
    if (!open || !aiAssistEnabled || !ghostImageUrl) {
      setAiReady(false);
      setAlignmentScore(0);
      return undefined;
    }
    let cancelled = false;

    const bootstrap = async () => {
      try {
        // Warm up the video-mode landmarker (also caches the model)
        await getPoseLandmarker();
        if (cancelled) return;

        // Pre-compute reference pose from the ghost image
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = ghostImageUrl;
        await new Promise((res, rej) => {
          img.onload = res;
          img.onerror = () => rej(new Error('Ghost image load failed'));
        });
        if (cancelled) return;
        const refPose = await detectPoseInImage(img);
        if (cancelled) return;
        referencePoseRef.current = refPose;
        if (!refPose) {
          // No body detected in the reference photo — silently disable AI
          setAiReady(false);
          return;
        }
        setAiReady(true);

        // Start the live detection loop
        const tick = () => {
          if (cancelled || lockedInRef.current) return;
          const v = videoRef.current;
          if (v && v.readyState >= 2 && videoRef.current && referencePoseRef.current) {
            const current = detectPose(v);
            if (current) {
              lastPoseRef.current = current;
              const score = poseSimilarity(current, referencePoseRef.current);
              setAlignmentScore(score);

              // Lock-in logic: start a 1.5s hold when crossing threshold
              if (score >= AUTO_THRESHOLD) {
                if (!lockInTimerRef.current && countdown === null && !capturing) {
                  lockInTimerRef.current = setTimeout(() => {
                    lockInTimerRef.current = null;
                    if (cancelled || lockedInRef.current) return;
                    lockedInRef.current = true;
                    setAutoTriggered(true);
                    // Kick off the normal shutter path (respects user's timer choice)
                    handleShutterRef.current?.();
                  }, AUTO_HOLD_MS);
                }
              } else if (lockInTimerRef.current) {
                clearTimeout(lockInTimerRef.current);
                lockInTimerRef.current = null;
              }
            }
          }
          rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
      } catch (err) {
        console.warn('[SelfTimerCamera] AI bootstrap failed:', err);
        setAiReady(false);
        ErrorLogger.log(err, 'Scan_Postural_Module', {
          step: 'blazepose_bootstrap',
          has_ghost_image: !!ghostImageUrl,
        });
      }
    };

    bootstrap();

    return () => {
      cancelled = true;
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
      if (lockInTimerRef.current) { clearTimeout(lockInTimerRef.current); lockInTimerRef.current = null; }
      referencePoseRef.current = null;
      lockedInRef.current = false;
      setAutoTriggered(false);
      setAlignmentScore(0);
    };
  }, [open, aiAssistEnabled, ghostImageUrl, countdown, capturing]);

  // Keep a ref of handleShutter so the AI loop can call it without dep on state
  const handleShutterRef = useRef(null);

  const flipCamera = async () => {
    const next = facing === 'user' ? 'environment' : 'user';
    setFacing(next);
    await startCamera(next);
  };

  // Actual snapshot from video → Blob → File
  const takeSnapshot = useCallback(async () => {
    if (!videoRef.current || !streamRef.current) return;
    setCapturing(true);
    try {
      const v = videoRef.current;
      const canvas = document.createElement('canvas');
      canvas.width  = v.videoWidth  || 1280;
      canvas.height = v.videoHeight || 720;
      const ctx = canvas.getContext('2d');
      // Mirror horizontally when using front camera (user expects selfie-mirror)
      if (facing === 'user') {
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
      }
      ctx.drawImage(v, 0, 0, canvas.width, canvas.height);

      const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.92));
      if (!blob) throw new Error('Canvas toBlob returned null');
      const file = new File([blob], `selfie_${Date.now()}.jpg`, { type: 'image/jpeg' });

      // Free canvas memory
      canvas.width = 0; canvas.height = 0;

      // Compute clinical posture metadata from the last live pose (if AI was active).
      // This runs silently — zero UX noise for the user.
      let postureMeta = null;
      if (lastPoseRef.current) {
        try { postureMeta = analyzePosture(lastPoseRef.current); }
        catch (e) { console.warn('[SelfTimerCamera] analyzePosture failed:', e); }
      }

      // Support both callback shapes: legacy (file) and clinical (file, meta)
      onCapture(file, {
        posture: postureMeta,
        alignment_score: aiReady ? alignmentScore : null,
        auto_captured: autoTriggered,
      });
      onClose();
    } catch (err) {
      console.error('[SelfTimerCamera] snapshot failed:', err);
      toast.error(fr ? "Capture impossible — l'équipe YUMIX a été avertie" : 'Capture failed — YUMIX team has been notified');
      setCapturing(false);
      ErrorLogger.log(err, 'Scan_Postural_Snapshot', {
        video_ready: !!videoRef.current?.videoWidth,
        facing,
        had_pose: !!lastPoseRef.current,
        severity: 'CRITICAL',
      });
    }
  }, [facing, onCapture, onClose, fr, aiReady, alignmentScore, autoTriggered]);

  // Shutter = either immediate or launches countdown
  const handleShutter = () => {
    if (capturing || countdown !== null) return;
    if (timerSec === 0) {
      takeSnapshot();
      return;
    }
    // Countdown
    let n = timerSec;
    setCountdown(n);
    countdownRef.current = setInterval(() => {
      n -= 1;
      if (n <= 0) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
        setCountdown(0);
        // Tiny shutter flash delay then snap
        setTimeout(() => {
          setCountdown(null);
          takeSnapshot();
        }, 250);
      } else {
        setCountdown(n);
      }
    }, 1000);
  };

  const cancelCountdown = () => {
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
    setCountdown(null);
  };

  // Expose handleShutter via ref so the AI alignment loop can call it
  useEffect(() => { handleShutterRef.current = handleShutter; });

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black flex flex-col"
      style={containerStyle}
      data-testid="self-timer-camera-modal"
    >
      {/* Top bar */}
      <div className="relative flex items-center justify-between px-3 py-2.5 bg-gradient-to-b from-black/90 to-transparent z-20 shrink-0">
        <button
          type="button"
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-black/60 backdrop-blur flex items-center justify-center text-white"
          data-testid="camera-close-btn"
          aria-label={fr ? 'Fermer' : 'Close'}
        >
          <X className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/60 backdrop-blur">
          <Timer className="w-4 h-4 text-yellow-400" />
          <span className="text-xs text-white font-mono uppercase">
            {timerSec === 0 ? 'OFF' : `${timerSec}s`}
          </span>
        </div>
        <button
          type="button"
          onClick={flipCamera}
          disabled={!ready}
          className="w-10 h-10 rounded-full bg-black/60 backdrop-blur flex items-center justify-center text-white disabled:opacity-40"
          data-testid="camera-flip-btn"
          aria-label={fr ? 'Changer caméra' : 'Flip camera'}
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Video preview */}
      <div className="flex-1 min-h-0 relative overflow-hidden">
        {error ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center text-white">
            <Camera className="w-12 h-12 mb-3 opacity-40" />
            <p className="text-sm mb-1">
              {error === 'NotAllowedError' || error === 'PermissionDeniedError'
                ? (fr ? 'Permission caméra refusée.' : 'Camera permission denied.')
                : (fr ? 'Caméra indisponible.' : 'Camera unavailable.')}
            </p>
            <p className="text-xs text-white/60 mb-5">
              {fr ? 'Active la caméra dans les paramètres de ton navigateur.' : 'Enable camera in your browser settings.'}
            </p>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-white text-black text-sm font-semibold"
              data-testid="camera-error-close"
            >
              {fr ? 'Fermer' : 'Close'}
            </button>
          </div>
        ) : awaitingUserGesture ? (
          // Iter. 404.e — iOS Safari camera gesture gate. The bigger and more
          // prominent the button, the higher the conversion to gesture-bound
          // getUserMedia call.
          <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center text-white bg-black/70">
            <Camera className="w-14 h-14 mb-4 text-amber-400" />
            <p className="text-lg font-semibold mb-2">
              {fr ? 'Active ta caméra' : 'Enable your camera'}
            </p>
            <p className="text-xs text-white/70 mb-6 max-w-xs leading-relaxed">
              {fr
                ? "iOS Safari demande un appui de ton doigt pour démarrer la caméra. Tape sur le bouton ci-dessous puis autorise l'accès."
                : 'iOS Safari requires a tap to start the camera. Tap below and grant permission.'}
            </p>
            <button
              type="button"
              onClick={() => {
                setAwaitingUserGesture(false);
                startCamera(facing);
              }}
              className="px-6 py-3 rounded-full bg-amber-500 hover:bg-amber-600 text-black text-sm font-bold shadow-lg active:scale-95 transition-transform"
              data-testid="camera-activate-gesture-btn"
            >
              🎥 {fr ? 'Activer la caméra' : 'Enable camera'}
            </button>
          </div>
        ) : (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
            style={{ transform: facing === 'user' ? 'scaleX(-1)' : 'none' }}
            data-testid="camera-preview"
          />
        )}

        {/* 👻 GHOST ALIGNMENT OVERLAY — tinted per alignment score.
            Opacity is FIXED at GHOST_FIXED_OPACITY (35%) for a friction-free
            experience: no slider, no toggle. The ghost is colored red/orange/green
            so the user sees at a glance how close their pose is. */}
        {!error && ghostImageUrl && (
          <>
            {/* Base ghost image (greyscaled so the tint reads cleanly) */}
            <img
              src={ghostImageUrl}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover pointer-events-none transition-opacity duration-200"
              style={{
                opacity: GHOST_FIXED_OPACITY,
                filter: 'grayscale(1) contrast(1.1)',
                transform: facing === 'user' ? 'scaleX(-1)' : 'none',
                zIndex: 10,
              }}
              data-testid="ghost-alignment-overlay"
            />
            {/* Alignment color wash on top of the ghost — red/orange/green
                based on the live BlazePose similarity score. */}
            {aiAssistEnabled && aiReady && (
              <div
                className="absolute inset-0 pointer-events-none transition-colors duration-300"
                style={{
                  mixBlendMode: 'multiply',
                  backgroundColor:
                    alignmentScore >= AUTO_THRESHOLD ? 'rgba(34, 197, 94, 0.55)'      // green 500
                    : alignmentScore >= 0.80         ? 'rgba(249, 115, 22, 0.55)'      // orange 500
                    :                                  'rgba(239, 68, 68, 0.55)',      // red 500
                  zIndex: 11,
                  maskImage: `url(${ghostImageUrl})`,
                  WebkitMaskImage: `url(${ghostImageUrl})`,
                  maskSize: 'cover',
                  WebkitMaskSize: 'cover',
                  maskPosition: 'center',
                  WebkitMaskPosition: 'center',
                  maskRepeat: 'no-repeat',
                  WebkitMaskRepeat: 'no-repeat',
                  transform: facing === 'user' ? 'scaleX(-1)' : 'none',
                }}
                data-testid="ghost-alignment-tint"
              />
            )}
          </>
        )}

        {/* Dynamic alignment instruction banner (top-center, above ghost) */}
        {!error && ghostImageUrl && aiAssistEnabled && countdown === null && !capturing && (
          <div
            className="absolute top-3 left-1/2 -translate-x-1/2 z-30 px-4 py-2 rounded-full bg-black/70 backdrop-blur text-white text-[12px] font-medium text-center max-w-[90%] shadow-lg"
            data-testid="alignment-instruction-banner"
          >
            {alignmentScore >= AUTO_THRESHOLD
              ? (fr ? '🎯 Parfait — maintiens la pose !' : '🎯 Perfect — hold it!')
              : alignmentScore >= 0.80
              ? (fr ? '🟠 Tu y es presque… ajuste légèrement' : '🟠 Almost there… small adjust')
              : (fr ? 'Aligne ton corps sur la silhouette fantôme pour déclencher la photo.' : 'Align your body on the ghost silhouette to trigger the photo.')}
          </div>
        )}

        {/* 🤖 AI PHOTOGRAPHER SCORE RING — top-right corner */}
        {!error && aiAssistEnabled && aiReady && ghostImageUrl && (
          <div
            className="absolute top-16 right-3 z-30 flex flex-col items-center"
            data-testid="ai-alignment-score"
          >
            <div className="relative w-14 h-14">
              <svg className="w-14 h-14 -rotate-90" viewBox="0 0 56 56">
                <circle cx="28" cy="28" r="24" className="fill-none stroke-white/20" strokeWidth="4" />
                <circle
                  cx="28" cy="28" r="24"
                  className={`fill-none transition-[stroke-dashoffset,stroke] duration-200 ${
                    alignmentScore >= AUTO_THRESHOLD
                      ? 'stroke-green-400'
                      : alignmentScore >= 0.75
                      ? 'stroke-yellow-400'
                      : 'stroke-white/60'
                  }`}
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeDasharray={2 * Math.PI * 24}
                  strokeDashoffset={2 * Math.PI * 24 * (1 - alignmentScore)}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center text-white font-mono text-[11px] font-bold">
                {Math.round(alignmentScore * 100)}%
              </div>
            </div>
            <span className="mt-1 text-[9px] uppercase tracking-wider text-white/80 font-semibold">
              {fr ? 'Alignement' : 'Alignment'}
            </span>
            {alignmentScore >= AUTO_THRESHOLD && !autoTriggered && (
              <span className="mt-0.5 text-[9px] text-green-400 font-bold animate-pulse">
                {fr ? '⏱ MAINTIENS...' : '⏱ HOLD...'}
              </span>
            )}
            {autoTriggered && (
              <span className="mt-0.5 text-[9px] text-green-400 font-bold">
                {fr ? '📸 AUTO' : '📸 AUTO'}
              </span>
            )}
          </div>
        )}

        {/* Giant countdown overlay */}
        {countdown !== null && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-30" data-testid="countdown-overlay">
            <div
              key={countdown}
              className="text-white font-heading font-bold drop-shadow-[0_0_30px_rgba(255,107,53,0.9)]"
              style={{
                fontSize: 'clamp(8rem, 40vw, 18rem)',
                lineHeight: 1,
                animation: 'countdown-pop 1s ease-out forwards',
              }}
            >
              {countdown > 0 ? countdown : '📸'}
            </div>
          </div>
        )}

        {/* Cancel countdown button */}
        {countdown !== null && countdown > 0 && (
          <button
            type="button"
            onClick={cancelCountdown}
            className="absolute bottom-8 left-1/2 -translate-x-1/2 px-5 py-2 rounded-full bg-white/20 backdrop-blur text-white text-xs uppercase tracking-wider z-40"
            data-testid="camera-cancel-countdown"
          >
            {fr ? 'Annuler' : 'Cancel'}
          </button>
        )}
      </div>

      {/* Bottom control bar */}
      {!error && (
        <div className="bg-gradient-to-t from-black/95 to-transparent pt-3 pb-4 sm:pt-4 sm:pb-6 px-4 z-20 shrink-0">
          {/* 🤖 AI Assist toggle (only shown when a previous photo exists) */}
          {ghostImageUrl && (
            <div className="flex items-center justify-center gap-2 mb-3" data-testid="ai-assist-controls">
              <button
                type="button"
                onClick={() => setAiAssistEnabled(v => !v)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-wider transition-all ${
                  aiAssistEnabled
                    ? 'bg-green-500/20 text-green-300 ring-1 ring-green-500/50'
                    : 'bg-white/10 text-white/60'
                }`}
                data-testid="ai-assist-toggle"
              >
                <Sparkles className={`w-3.5 h-3.5 ${aiAssistEnabled ? 'text-green-400' : ''}`} />
                {aiAssistEnabled
                  ? (fr ? `Photographe IA ${aiReady ? '✓' : '…'}` : `AI Photographer ${aiReady ? '✓' : '…'}`)
                  : (fr ? 'Activer IA' : 'Enable AI')
                }
              </button>
            </div>
          )}

          {/* 👻 Ghost alignment now fully automatic — no manual controls */}

          {/* Timer options */}
          <div className="flex items-center justify-center gap-2 mb-3 sm:mb-5">
            {TIMER_OPTIONS.map(sec => (
              <button
                key={sec}
                type="button"
                onClick={() => setTimerSec(sec)}
                disabled={countdown !== null || capturing}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider transition-all ${
                  timerSec === sec
                    ? 'bg-yellow-400 text-black scale-105'
                    : 'bg-white/10 text-white/80 hover:bg-white/20'
                }`}
                data-testid={`timer-option-${sec}`}
              >
                {sec === 0 ? (fr ? 'OFF' : 'OFF') : `${sec}s`}
              </button>
            ))}
          </div>

          {/* Shutter */}
          <div className="flex items-center justify-center">
            <button
              type="button"
              onClick={handleShutter}
              disabled={!ready || capturing || countdown !== null}
              className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white flex items-center justify-center disabled:opacity-40 active:scale-95 transition-transform ring-4 ring-white/30"
              data-testid="camera-shutter-btn"
              aria-label={fr ? 'Prendre la photo' : 'Take photo'}
            >
              {capturing ? (
                <Check className="w-7 h-7 sm:w-8 sm:h-8 text-green-600" />
              ) : (
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-white border-4 border-black/10" />
              )}
            </button>
          </div>
          <p className="text-[11px] text-white/60 text-center mt-3 uppercase tracking-wider">
            {timerSec === 0
              ? (fr ? 'Appuie sur le bouton' : 'Tap the shutter')
              : (fr ? `Décompte ${timerSec}s activé` : `${timerSec}s timer on`)}
          </p>
        </div>
      )}

      {/* 🎓 First-run onboarding overlay — 3-point explainer.
          Shown only once per device (localStorage). Explains how Ghost Alignment
          and Auto-Shutter work before the user interacts with the camera. */}
      {showOnboarding && !error && (
        <div
          className="absolute inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-5"
          data-testid="camera-onboarding-overlay"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-sm bg-gradient-to-b from-neutral-900 to-neutral-950 border border-white/15 rounded-2xl p-6 space-y-5 shadow-2xl">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              <h3 className="font-heading uppercase tracking-wider text-white text-base">
                {fr ? 'Mode Scan automatique' : 'Auto-scan mode'}
              </h3>
            </div>

            <ul className="space-y-3.5 text-[13px] text-white/90 leading-relaxed">
              <li className="flex gap-3" data-testid="onboarding-step-1">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/25 text-primary flex items-center justify-center font-bold text-xs ring-1 ring-primary/60">1</span>
                <div>
                  <strong className="text-white">
                    {fr ? 'Silhouette fantôme' : 'Ghost silhouette'}
                  </strong>
                  <p className="text-white/70 mt-0.5">
                    {fr
                      ? 'Ta photo précédente apparaît en transparence. Sers-t\'en comme repère pour te positionner.'
                      : 'Your previous photo shows at low opacity. Use it as a guide to line yourself up.'}
                  </p>
                </div>
              </li>
              <li className="flex gap-3" data-testid="onboarding-step-2">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/25 text-primary flex items-center justify-center font-bold text-xs ring-1 ring-primary/60">2</span>
                <div>
                  <strong className="text-white">
                    {fr ? 'Code couleur d\'alignement' : 'Color-coded alignment'}
                  </strong>
                  <p className="text-white/70 mt-0.5">
                    <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-1 align-middle" /> {fr ? 'Rouge : décalé' : 'Red: off'}
                    {' · '}
                    <span className="inline-block w-2 h-2 rounded-full bg-orange-500 mr-1 align-middle" /> {fr ? 'Orange : proche (80%+)' : 'Orange: close (80%+)'}
                    {' · '}
                    <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1 align-middle" /> {fr ? 'Vert : parfait (95%+)' : 'Green: perfect (95%+)'}
                  </p>
                </div>
              </li>
              <li className="flex gap-3" data-testid="onboarding-step-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/25 text-primary flex items-center justify-center font-bold text-xs ring-1 ring-primary/60">3</span>
                <div>
                  <strong className="text-white">
                    {fr ? 'Déclenchement auto' : 'Auto-shutter'}
                  </strong>
                  <p className="text-white/70 mt-0.5">
                    {fr
                      ? 'Quand tu es aligné à 95 %+ pendant 1,5 s, la photo se prend toute seule. Zéro friction.'
                      : 'Hold 95 %+ alignment for 1.5 s — the shot is taken for you. Zéro friction.'}
                  </p>
                </div>
              </li>
            </ul>

            <button
              type="button"
              onClick={() => {
                try { window.localStorage?.setItem(ONBOARDING_STORAGE_KEY, '1'); } catch (_) {}
                setShowOnboarding(false);
              }}
              className="w-full h-11 rounded-full bg-primary text-primary-foreground font-heading uppercase tracking-[0.2em] text-sm hover:bg-primary/90 active:scale-[0.98] transition"
              data-testid="onboarding-dismiss-btn"
            >
              {fr ? 'C\'est compris' : 'Got it'}
            </button>
          </div>
        </div>
      )}

      {/* Inline keyframe for countdown pop */}
      <style>{`
        @keyframes countdown-pop {
          0%   { transform: scale(0.5); opacity: 0; }
          15%  { transform: scale(1.15); opacity: 1; }
          70%  { transform: scale(1); opacity: 1; }
          100% { transform: scale(0.9); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
